/**
 * A swappable, env-gated human-verification (captcha) widget (P2-26).
 *
 * Wraps a Cloudflare Turnstile (default) or hCaptcha widget — selected by env
 * via {@link getCaptchaConfig} — and exposes the obtained token to callers
 * through both an {@link CaptchaGateProps.onVerify} callback AND the shared
 * token store ({@link setCaptchaToken}), which the {@link authClient} bond and
 * the guest-project on-ramp read when issuing guarded requests.
 *
 * Inert without keys: when no site key is configured (dev/local) the component
 * renders NOTHING and reports "passed" once via `onVerify('')`, so the
 * zero-friction local/demo flow is completely unaffected.
 *
 * The chrome around the widget follows the IDE-shell convention (theme-token
 * CSS variables in inline styles, like {@link ToolbarIconButton}); the provider
 * renders its own iframe into the container, so only the surrounding error/retry
 * affordance is ours. The retry itself is a LABELLED CTA and therefore a real
 * design-system button (`cm.button` + `cm.touchTargetCompact`), identical to
 * every other Retry on the platform — see molecule/DESIGN.md → "CTA color
 * semantics". No hardcoded hex beyond a `var()` fallback. `data-mol-id` is set
 * on the container, the widget mount, and the retry button.
 *
 * Every render path here is behind a lazy boundary (Signup, Workspace, the
 * dynamically-imported GlobalAuthModal), so `getClassMap()` is always wired by
 * the time this renders — see `__tests__/eager-classmap-gate.test.ts`.
 */

import { type CSSProperties, useEffect, useRef, useState } from 'react'

import { useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'

import {
  type CaptchaProvider,
  clearCaptchaToken,
  getCaptchaConfig,
  setCaptchaToken,
} from './captcha.js'

/**
 * The subset of the Turnstile / hCaptcha explicit-render JS API this widget
 * uses. Both providers expose the same shape, so one interface drives both.
 */
interface CaptchaWidgetApi {
  /** Render a widget into `container`; returns the widget id used to reset/remove it. */
  render: (container: HTMLElement, options: CaptchaRenderOptions) => string
  /** Reset a rendered widget so it issues a fresh challenge/token. */
  reset: (widgetId?: string) => void
  /** Remove a rendered widget (Turnstile/hCaptcha both support this). */
  remove?: (widgetId: string) => void
}

/** The explicit-render options both providers accept. */
interface CaptchaRenderOptions {
  /** The public site key. */
  sitekey: string
  /** Fired with the verification token when the challenge is solved. */
  callback: (token: string) => void
  /** Fired when a previously-issued token expires. */
  'expired-callback'?: () => void
  /** Fired when the widget hits an unrecoverable error. */
  'error-callback'?: () => void
  /** Optional action label for the provider's analytics/segmentation. */
  action?: string
  /** Follow the page theme. */
  theme?: 'auto' | 'light' | 'dark'
}

declare global {
  // The provider script attaches its API to one of these globals at runtime.
  // Optional because it only exists after the script has loaded.
  interface Window {
    turnstile?: CaptchaWidgetApi
    hcaptcha?: CaptchaWidgetApi
  }
}

/** The explicit-render script URL for each provider. */
const SCRIPT_SRC: Record<CaptchaProvider, string> = {
  turnstile: 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit',
  hcaptcha: 'https://js.hcaptcha.com/1/api.js?render=explicit',
}

/** In-flight script loads, deduped per provider so the tag is injected once. */
const scriptLoads: Partial<Record<CaptchaProvider, Promise<void>>> = {}

/** The runtime API object for a provider, once its script has loaded. */
function widgetApiFor(provider: CaptchaProvider): CaptchaWidgetApi | undefined {
  return provider === 'hcaptcha' ? window.hcaptcha : window.turnstile
}

/**
 * Load the provider's explicit-render script once (resolving immediately if its
 * API is already present). A failed load clears the cache so a retry re-injects.
 *
 * @param provider - The captcha provider whose script to load.
 * @returns A promise that resolves when the provider API is available.
 */
function loadCaptchaScript(provider: CaptchaProvider): Promise<void> {
  if (widgetApiFor(provider)) return Promise.resolve()
  const inFlight = scriptLoads[provider]
  if (inFlight) return inFlight
  const load = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT_SRC[provider]
    script.async = true
    script.defer = true
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () => {
      // Allow a later retry to re-inject the tag rather than reuse a dead load.
      delete scriptLoads[provider]
      reject(new Error(`Failed to load ${provider} captcha script`))
    })
    document.head.appendChild(script)
  })
  scriptLoads[provider] = load
  return load
}

/** Props for the {@link CaptchaGate} widget. */
export interface CaptchaGateProps {
  /**
   * Called with the verification token when the challenge is solved. When
   * captcha is NOT configured (dev/local) it is called once with `''` to signal
   * the gate is inert/"passed" — so a parent gate can treat any `onVerify` as
   * "cleared to proceed".
   */
  onVerify?: (token: string) => void
  /** Called when a previously-obtained token expires (treat as un-verified). */
  onExpire?: () => void
  /** Optional provider `action` label for analytics/segmentation. */
  action?: string
  /** `data-mol-id` base for the container/widget/retry elements. */
  molId?: string
  /** Extra class for the container. */
  className?: string
  /** Extra inline styles merged onto the container. */
  style?: CSSProperties
}

/**
 * Render the captcha challenge, or nothing when captcha is unconfigured.
 * @param root0 - Component props.
 * @param root0.onVerify - Token callback (called with `''` when inert/passed).
 * @param root0.onExpire - Token-expiry callback.
 * @param root0.action - Provider action label.
 * @param root0.molId - `data-mol-id` base.
 * @param root0.className - Extra container class.
 * @param root0.style - Extra container inline styles.
 * @returns The rendered widget, or `null` when captcha is not configured.
 */
export function CaptchaGate({
  onVerify,
  onExpire,
  action,
  molId = 'captcha-gate',
  className,
  style,
}: CaptchaGateProps) {
  const { t } = useTranslation()
  const cm = getClassMap()
  // Resolve config once — env is static for the lifetime of the app.
  const [config] = useState(() => getCaptchaConfig())
  const containerRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<string | null>(null)
  const [failed, setFailed] = useState(false)
  // Bumped by Retry to force the render effect to re-run.
  const [attempt, setAttempt] = useState(0)

  // Keep callbacks fresh without retriggering the render effect on every render.
  const onVerifyRef = useRef(onVerify)
  onVerifyRef.current = onVerify
  const onExpireRef = useRef(onExpire)
  onExpireRef.current = onExpire

  // Inert path: no site key configured → report "passed" once, render nothing.
  useEffect(() => {
    if (config) return
    onVerifyRef.current?.('')
  }, [config])

  // Configured path: load the provider script + render the widget.
  useEffect(() => {
    if (!config) return
    let cancelled = false
    setFailed(false)
    loadCaptchaScript(config.provider)
      .then(() => {
        if (cancelled) return
        const api = widgetApiFor(config.provider)
        const el = containerRef.current
        if (!api || !el) {
          setFailed(true)
          return
        }
        try {
          widgetIdRef.current = api.render(el, {
            sitekey: config.siteKey,
            action,
            theme: 'auto',
            callback: (token) => {
              setCaptchaToken(token)
              onVerifyRef.current?.(token)
            },
            'expired-callback': () => {
              clearCaptchaToken()
              onExpireRef.current?.()
            },
            'error-callback': () => {
              clearCaptchaToken()
              setFailed(true)
            },
          })
        } catch (error) {
          console.error('CaptchaGate: widget render failed', error)
          setFailed(true)
        }
      })
      .catch((error) => {
        if (cancelled) return
        console.error('CaptchaGate: captcha script load failed', error)
        setFailed(true)
      })
    return () => {
      cancelled = true
      const api = widgetApiFor(config.provider)
      if (api?.remove && widgetIdRef.current) {
        try {
          api.remove(widgetIdRef.current)
        } catch (_error) {
          // Widget already torn down by the provider — nothing to clean up.
        }
      }
      widgetIdRef.current = null
      // A token tied to an unmounted widget is no longer presentable.
      clearCaptchaToken()
    }
  }, [config, action, attempt])

  // Inert: render nothing so the dev/local on-ramp is visually unchanged.
  if (!config) return null

  return (
    <div
      data-mol-id={molId}
      className={className}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, ...style }}
    >
      {/* The provider renders its own (themed) iframe into this mount. */}
      <div ref={containerRef} data-mol-id={`${molId}-widget`} style={{ minHeight: 65 }} />
      {failed && (
        <div
          data-mol-id={`${molId}-error`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            color: 'var(--mol-color-error, #ef4444)',
          }}
        >
          <span>
            {t('captcha.error', undefined, {
              defaultValue: "Couldn't load the human-verification check.",
            })}
          </span>
          <button
            type="button"
            data-mol-id={`${molId}-retry`}
            onClick={() => {
              setFailed(false)
              setAttempt((n) => n + 1)
            }}
            className={cm.cn(
              cm.button({ variant: 'solid', color: 'primary', size: 'xs' }),
              cm.touchTargetCompact,
            )}
          >
            {t('common.retry', undefined, { defaultValue: 'Retry' })}
          </button>
        </div>
      )}
    </div>
  )
}
